"use client";

import type { PostHog } from "posthog-js";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect } from "react";
import {
  ANALYTICS_EVENTS,
  type FeatureGateHitProps,
  type GatedFeature,
  type PaywallTrigger,
  type PaywallViewedProps,
} from "@/lib/analytics/events";

type PaywallViewed = {
  trigger: PaywallTrigger;
  feature?: GatedFeature | null;
  plansShown: string[];
};

/** Captures `paywall_viewed`, for prompts that don't mount a component. */
export function capturePaywallViewed(
  posthog: PostHog,
  { trigger, feature = null, plansShown }: PaywallViewed,
) {
  posthog?.capture(ANALYTICS_EVENTS.paywallViewed, {
    trigger,
    feature,
    plans_shown: plansShown,
  } satisfies PaywallViewedProps);
}

/** Fires `paywall_viewed` once each time the prompt mounts or `enabled` turns on. */
export function usePaywallViewed({
  trigger,
  feature = null,
  plansShown,
  enabled = true,
}: PaywallViewed & { enabled?: boolean }) {
  const posthog = usePostHog();
  // Joined so an inline array literal doesn't re-fire the effect every render.
  const plansKey = plansShown.join(",");

  // biome-ignore lint/correctness/useExhaustiveDependencies: plansKey stands in for plansShown
  useEffect(() => {
    if (!enabled) return;
    capturePaywallViewed(posthog, { trigger, feature, plansShown });
  }, [enabled, posthog, trigger, feature, plansKey]);
}

/** Returns a `feature_gate_hit` tracker for clicks on padlocked options. */
export function useTrackFeatureGate() {
  const posthog = usePostHog();
  return useCallback(
    (feature: GatedFeature, location: string) => {
      posthog?.capture(ANALYTICS_EVENTS.featureGateHit, {
        feature,
        location,
      } satisfies FeatureGateHitProps);
    },
    [posthog],
  );
}
